// First-party, anonymous Google Ads visits. No account, file, prompt or URL query data.
const API = process.env.REACT_APP_API_URL || "";
const SESSION_KEY = "cookistory.adVisit.v1";
const VISITOR_KEY = "cookistory.adVisitor.v1";
const PENDING_PREFIX = "cookistory.adPending.v1.";
// Stay inside the server's one-day acceptance window, including retry time.
const PENDING_TTL = 23 * 60 * 60 * 1000;
const IDLE = 30 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
let allowed = false;
let current = null;
let path = "/";
let queue = [];
let sending = false;
let retryAfter = 0;
let segmentStart = 0;
let measured = false;
let lastInteraction = 0;
let initialized = false;
let lastPersisted = 0;
let firstMeasurementTimer;

function id() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(x => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function read(key) { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } }
function write(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* memory fallback */ } }
function removePending(id) { try { localStorage.removeItem(PENDING_PREFIX + id); } catch { /* storage unavailable */ } }
function validPending(item) {
  const s = item?.session, e = item?.event;
  return s && e && UUID.test(s.id) && UUID.test(s.visitor_id) && UUID.test(e.id)
    && Object.keys(s).sort().join() === "attribution,device,entry_path,id,visitor_id"
    && Object.keys(e).sort().join() === "active_ms,id,kind,occurred_at,path"
    && ["gclid", "gbraid", "wbraid", "utm"].includes(s.attribution)
    && ["mobile", "desktop"].includes(s.device)
    && ["page_view", "engagement", "design_generated", "stl_download", "quote_issued", "store_open"].includes(e.kind)
    && [s.entry_path, e.path].every(p => typeof p === "string" && p !== "admin" && pageCategory(p) === p)
    && Number.isInteger(e.active_ms) && e.active_ms >= 0 && e.active_ms <= 30000
    && (e.kind === "engagement" || e.active_ms === 0)
    && Date.parse(e.occurred_at) > Date.now() - PENDING_TTL && Date.parse(e.occurred_at) <= Date.now() + 5 * 60000;
}
function restorePending() {
  try {
    // One key per event prevents one tab overwriting another tab's unsent data.
    for (const key of Object.keys(localStorage).filter(k => k.startsWith(PENDING_PREFIX))) {
      const item = read(key);
      if (!validPending(item) || key !== PENDING_PREFIX + item.event.id) { localStorage.removeItem(key); continue; }
      if (!queue.some(x => x.event.id === item.event.id)) queue.push(item);
    }
  } catch { /* memory fallback */ }
  prunePending();
}
function prunePending() {
  queue = queue.filter(item => {
    if (validPending(item)) return true;
    removePending(item.event.id); return false;
  }).sort((a, b) => Date.parse(a.event.occurred_at) - Date.parse(b.event.occurred_at));
  while (queue.length > 100) removePending(queue.shift().event.id);
}
function acknowledge(group) {
  const ids = new Set(group.map(x => x.event.id));
  queue = queue.filter(x => !ids.has(x.event.id));
  ids.forEach(removePending);
}
function batchBody(group) {
  return JSON.stringify({ session: group[0].session, events: group.map(x => x.event) });
}

function flushOnExit() {
  if (!allowed) return;
  prunePending();
  // Do not wait for an in-flight request or backoff while the page is exiting.
  let pending = [...queue];
  while (pending.length) {
    const group = pending.filter(x => x.session.id === pending[0].session.id).slice(0, 20);
    const ids = new Set(group.map(x => x.event.id));
    pending = pending.filter(x => !ids.has(x.event.id));
    const body = batchBody(group);
    try {
      // The API parses JSON regardless of content type. text/plain avoids a
      // cross-origin preflight at exit. Beacon acceptance is NOT a server ack.
      if (navigator.sendBeacon?.(`${API}/api/analytics/ads/events`, new Blob([body], { type: "text/plain;charset=UTF-8" }))) continue;
    } catch { /* try keepalive below */ }
    try {
      void fetch(`${API}/api/analytics/ads/events`, { method: "POST", headers: { "Content-Type": "text/plain;charset=UTF-8" }, body, keepalive: true })
        .then(response => { if (response.ok) acknowledge(group); }).catch(() => {});
    } catch { /* retained for the next visit */ }
  }
}

export function pageCategory(value) {
  const p = String(value || "/").split(/[?#]/)[0];
  if (p === "/") return "/";
  if (/^\/admin(?:\/|$)/.test(p)) return "admin";
  if (/^\/gallery\/[^/]+/.test(p)) return "/gallery/detail";
  if (/^\/embed\/order(?:\/|$)/.test(p)) return "/embed/order";
  const top = p.split("/")[1];
  return ["design", "cutter", "gallery", "order", "pricing", "guide", "printing-faq", "login", "register", "mypage"].includes(top) ? `/${top}` : "/other";
}

export function googleAdEntry(href) {
  const url = new URL(href);
  const q = new URLSearchParams(url.search);
  // HashRouter URLs may put campaign parameters after #/ as well.
  const hashQuery = url.hash.indexOf("?");
  if (hashQuery >= 0) new URLSearchParams(url.hash.slice(hashQuery + 1)).forEach((v, k) => { if (!q.has(k)) q.set(k, v); });
  for (const key of ["gclid", "gbraid", "wbraid"]) if (q.get(key)?.trim()) return key;
  return q.get("utm_source")?.toLowerCase() === "google" && ["cpc", "ppc", "paidsearch", "paid", "display"].includes(q.get("utm_medium")?.toLowerCase()) ? "utm" : null;
}

function visit() {
  if (!allowed || path === "admin") return null;
  const now = Date.now();
  if (current && now - current.lastActivity < IDLE) return current;
  const saved = read(SESSION_KEY);
  if (saved && UUID.test(saved.id) && UUID.test(saved.visitor_id) && ["gclid", "gbraid", "wbraid", "utm"].includes(saved.attribution) && now >= saved.lastActivity && now - saved.lastActivity < IDLE) {
    current = saved; return current;
  }
  current = null;
  const attribution = googleAdEntry(window.location.href);
  if (!attribution) return null;
  let visitor = read(VISITOR_KEY);
  if (!visitor || !UUID.test(visitor.id) || visitor.expires < now) visitor = { id: id(), expires: now + 90 * 86400000 };
  write(VISITOR_KEY, visitor);
  current = { id: id(), visitor_id: visitor.id, attribution, device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop", entry_path: path, lastActivity: now };
  write(SESSION_KEY, current);
  return current;
}

function record(kind, activeMs = 0) {
  // Background timers must never create a new visit after 30 minutes of inactivity.
  if (kind === "engagement" && (!current || Date.now() - current.lastActivity >= IDLE)) return;
  const session = visit();
  if (!session) return;
  // Heartbeats do not prolong an idle session; only navigation/user actions do.
  if (kind !== "engagement") { session.lastActivity = Date.now(); write(SESSION_KEY, session); }
  const { lastActivity, ...publicSession } = session;
  queue.push({ session: publicSession, event: { id: id(), kind, path, active_ms: Math.min(30000, Math.max(0, Math.round(activeMs))), occurred_at: new Date().toISOString() } });
  const item = queue[queue.length - 1];
  write(PENDING_PREFIX + item.event.id, item);
  prunePending();
}

export async function flushAdEvents() {
  prunePending();
  if (sending || !queue.length || !allowed || Date.now() < retryAfter) return;
  const group = queue.filter(x => x.session.id === queue[0].session.id).slice(0, 20);
  sending = true;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(`${API}/api/analytics/ads/events`, { method: "POST", headers: { "Content-Type": "application/json" }, body: batchBody(group), keepalive: true, signal: controller.signal });
    if (response.ok || [400, 403, 422].includes(response.status)) {
      acknowledge(group);
    } else retryAfter = Date.now() + 60000;
  } catch { retryAfter = Date.now() + 10000; }
  finally {
    clearTimeout(timeout); sending = false;
    if (queue.length && allowed && Date.now() >= retryAfter) void flushAdEvents();
  }
}

function engagement() {
  if (!segmentStart) return;
  const end = Math.min(Date.now(), lastInteraction + 60000);
  const ms = Math.max(0, end - segmentStart);
  // A measured zero is different from a page that disappeared before a heartbeat.
  if (ms || !measured) { record("engagement", ms); measured = true; }
  segmentStart = document.visibilityState === "visible" ? Date.now() : 0;
}

export function trackAdActivity(kind) {
  if (!["design_generated", "stl_download", "quote_issued", "store_open"].includes(kind)) return;
  try { lastInteraction = Date.now(); record(kind); void flushAdEvents(); } catch { /* analytics must never break the service */ }
}

export function setAdTrackingAllowed(value) {
  const wasAllowed = allowed;
  allowed = Boolean(value);
  if (!allowed) {
    if (wasAllowed) acknowledge(queue);
    queue = []; current = null; segmentStart = 0;
    clearTimeout(firstMeasurementTimer);
  } else if (!wasAllowed) { restorePending(); retryAfter = 0; }
}

export function trackAdPage(value) {
  try {
    if (!allowed) return;
    engagement();
    path = pageCategory(value);
    segmentStart = 0;
    if (path === "admin") return;
    lastInteraction = Date.now(); measured = false;
    segmentStart = document.visibilityState === "visible" ? Date.now() : 0;
    record("page_view"); void flushAdEvents();
    clearTimeout(firstMeasurementTimer);
    firstMeasurementTimer = setTimeout(() => {
      if (allowed) { engagement(); void flushAdEvents(); }
    }, 1000);
    if (!initialized) {
      initialized = true;
      ["pointerdown", "keydown", "scroll"].forEach(name => window.addEventListener(name, () => {
        if (!allowed) return;
        lastInteraction = Date.now();
        if (current && lastInteraction - current.lastActivity < IDLE) {
          current.lastActivity = lastInteraction;
          if (lastInteraction - lastPersisted > 5000) { write(SESSION_KEY, current); lastPersisted = lastInteraction; }
        }
      }, { passive: true }));
      document.addEventListener("visibilitychange", () => {
        if (!allowed) return;
        engagement();
        if (document.visibilityState === "hidden") flushOnExit();
        else { lastInteraction = Date.now(); segmentStart = Date.now(); retryAfter = 0; void flushAdEvents(); }
      });
      window.addEventListener("pagehide", () => { engagement(); segmentStart = 0; flushOnExit(); });
      window.addEventListener("pageshow", () => {
        if (allowed && document.visibilityState === "visible") { lastInteraction = Date.now(); segmentStart = Date.now(); retryAfter = 0; void flushAdEvents(); }
      });
      window.addEventListener("online", () => { if (allowed) { retryAfter = 0; engagement(); void flushAdEvents(); } });
      setInterval(() => { if (allowed) { engagement(); void flushAdEvents(); } }, 15000);
    }
  } catch { /* private browsing / blocked storage / unavailable crypto */ }
}
